/**
 * FAQ.jsx — Luvia Frequently Asked Questions
 *
 * Full audit fixes:
 * 1. Free shipping threshold policy update
 * 2. <details>/<summary> replaced with controlled accordion — animatable,
 *    consistent cross-browser, single-open enforcement optional
 * 3. <li> self-closing void element fixed — valid HTML now
 * 4. IconCheck replaced with correct contextual icons per card
 * 5. FAQ schema capped at 10 entries + moved to module level (no render cost)
 * 6. JSON-LD stringified once at module level
 * 7. getCategoryLabel → Map lookup, groupedFAQs computed in single useMemo pass
 * 8. <section> on search/filter → <div>/<form> — correct landmark semantics
 * 9. Desktop: two-column layout (sticky sidebar + content), not narrow column
 * 10. faq-categories__wrapper: flex-wrap removed — proper single-row scroll
 * 11. Touch targets: all interactive elements ≥ 44px
 * 12. og:image added
 */

import { useState, useMemo, useCallback, useId } from 'react';
import { Link }      from 'react-router-dom';
import { Helmet }    from 'react-helmet-async';
import Layout        from '../../components/common/Layout/Layout';
import { BRAND }     from '../../config/brand';
import './FAQ.css';

// ── Constants ──────────────────────────────────────────────────────────────
const SITE_URL  = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';
const FAQ_URL   = `${SITE_URL}/faq`;
const OG_IMAGE  = `${SITE_URL}/og-image.jpg`;

const FAQ_CATEGORIES = [
  { id: 'all',      label: 'All' },
  { id: 'orders',   label: 'Orders & Payment' },
  { id: 'shipping', label: 'Shipping' },
  { id: 'returns',  label: 'Returns Policy' },
  { id: 'size',     label: 'Size & Fit' },
  { id: 'products', label: 'Products' },
  { id: 'account',  label: 'Account' },
];

const CATEGORY_MAP = new Map(FAQ_CATEGORIES.map(c => [c.id, c.label]));

// ── FAQ Data ───────────────────────────────────────────────────────────────
// Free shipping on orders above 500
const FAQ_DATA = [
  // Orders & Payment
  {
    id: 'o1', category: 'orders',
    q: 'How do I place an order?',
    a: 'Browse our collection, select your size and colour, then click "Add to Bag". Review your cart and proceed to checkout. You can pay via credit/debit card, UPI, or net banking.',
  },
  {
    id: 'o2', category: 'orders',
    q: 'How do I track my order?',
    a: 'Once your order is dispatched you\'ll receive a tracking number by email. Track it on the courier\'s website or in your account under "My Orders".',
  },
  {
    id: 'o3', category: 'orders',
    q: 'Can I modify or cancel my order?',
    a: 'Orders can be modified or cancelled within 1 hour of placement. After that, contact us via WhatsApp. If already dispatched, cancellation is no longer possible.',
  },
  {
    id: 'o4', category: 'orders',
    q: 'What payment methods do you accept?',
    a: 'All major credit and debit cards, UPI, net banking, and digital wallets. Payments are processed through a secure encrypted gateway.',
  },
  {
    id: 'o5', category: 'orders',
    q: 'Is my payment information secure?',
    a: 'Yes. Payments are processed through PCI-DSS-compliant gateways. We never store full card details on our servers.',
  },
  {
    id: 'o6', category: 'orders',
    q: 'Will I receive an order confirmation?',
    a: 'Yes — an email confirmation with your order number and details is sent immediately after purchase.',
  },

  // Shipping
  {
    id: 's1', category: 'shipping',
    q: 'How long does shipping take?',
    a: 'Standard delivery takes 7–10 business days from dispatch. Orders are typically dispatched within 1–2 business days of payment confirmation.',
  },
  {
    id: 's2', category: 'shipping',
    q: 'Is shipping free?',
    a: 'We offer free shipping on orders above 500.',
  },
  {
    id: 's3', category: 'shipping',
    q: 'Do you ship internationally?',
    a: 'Currently we ship within India only — all major cities, towns, and rural areas.',
  },
  {
    id: 's4', category: 'shipping',
    q: 'What if I\'m not available to receive my order?',
    a: 'Our delivery partner attempts delivery up to 3 times. They\'ll leave a contact number to reschedule, or you can collect from the nearest courier facility.',
  },
  {
    id: 's5', category: 'shipping',
    q: 'Is my order insured during shipping?',
    a: 'Yes — all orders are fully insured in transit. Contact support immediately if your order arrives damaged or is lost.',
  },

  // Returns
  {
    id: 'r1', category: 'returns',
    q: 'Do you accept returns or exchanges right now?',
    a: 'Returns and exchanges are currently unavailable while we refine operations and quality control.',
  },
  {
    id: 'r2', category: 'returns',
    q: 'Why are returns currently unavailable?',
    a: 'As a growing jewelry brand, we are focusing on service stability and product consistency first. We will announce return availability once the policy reopens.',
  },
  {
    id: 'r3', category: 'returns',
    q: 'What if I receive a damaged or defective item?',
    a: 'Please contact support immediately on WhatsApp with your order number and clear photos. Our team will review and resolve the issue on priority.',
  },
  {
    id: 'r4', category: 'returns',
    q: 'Can I exchange for a different size?',
    a: 'Size exchanges are currently unavailable under the temporary no-return policy. Please use our Size Guide before placing your order.',
  },
  {
    id: 'r5', category: 'returns',
    q: 'Will I receive a refund if I no longer want the product?',
    a: 'Refunds for change-of-mind requests are not available at this time because returns are temporarily closed.',
  },
  {
    id: 'r6', category: 'returns',
    q: 'Where can I verify policy updates?',
    a: 'For the latest policy updates, check our FAQ, Shipping page, and Contact page, or message us on WhatsApp before ordering.',
  },

  // Size & Fit
  {
    id: 'sz1', category: 'size',
    q: 'What if I\'m between ring sizes?',
    a: 'We recommend sizing up for rings. For bracelets and necklaces, most pieces are adjustable or come in One Size. See our Size Guide for ring sizing.',
  },
  {
    id: 'sz2', category: 'size',
    q: 'Do your ring sizes run small or large?',
    a: 'True to standard Indian ring sizing. Our size chart matches most jewelry retailers. When in doubt, measure your finger or consult our Size Guide.',
  },
  {
    id: 'sz3', category: 'size',
    q: 'Are your pieces adjustable?',
    a: 'Many bracelets and anklets are adjustable. Product pages indicate which pieces offer adjustable sizing. Care instructions are provided with each order.',
  },
  {
    id: 'sz4', category: 'size',
    q: 'How do I measure myself accurately?',
    a: 'For rings: wrap a strip of paper around your finger, mark where it meets, measure in mm and match our Size Guide. For chains: measure a necklace you love. For bangles: wrap a tape around your wrist (or over your hand). See our Size Guide for detailed charts.',
  },

  // Products
  {
    id: 'p1', category: 'products',
    q: 'What materials are your jewelry made from?',
    a: 'We use brass, alloy, and copper with gold, rose gold, silver, and gunmetal plating. Many pieces feature cubic zirconia, pearls, or crystals.',
  },
  {
    id: 'p2', category: 'products',
    q: 'How should I care for my jewelry?',
    a: 'Store in a dry place. Avoid contact with water, perfume, and chemicals. Clean with a soft cloth. Remove before swimming or showering.',
  },
  {
    id: 'p3', category: 'products',
    q: 'Are your products sustainable?',
    a: 'We work with ethical suppliers and select materials responsibly. Visit our Sustainability page to learn more.',
  },
  {
    id: 'p4', category: 'products',
    q: 'How do I know if a product is in stock?',
    a: 'Stock availability is shown on each product page. Out-of-stock sizes show a "Notify Me" option so you can be alerted when back in stock.',
  },

  // Account
  {
    id: 'a1', category: 'account',
    q: 'How do I create an account?',
    a: 'Click "Sign In" in the navigation, then "Create Account". Sign up with email or use Google for faster registration.',
  },
  {
    id: 'a2', category: 'account',
    q: 'How do I reset my password?',
    a: 'Click "Sign In" → "Forgot Password". Enter your email to receive a reset link.',
  },
  {
    id: 'a3', category: 'account',
    q: 'Can I view my order history?',
    a: 'Yes — log in and go to "My Account" → "My Orders" to view all past orders and track shipments.',
  },
  {
    id: 'a4', category: 'account',
    q: 'Can I save multiple delivery addresses?',
    a: 'Yes — go to "My Addresses" in your account dashboard to add, edit, or delete saved addresses.',
  },
];

// ── FAQ schema — top 10 entries at module level (Google recommends ≤10 for rich results) ─
const FAQ_SCHEMA_IDS = ['o1','o2','s1','s2','r1','r2','r4','sz1','sz3','p1'];
const FAQ_SCHEMA_ENTRIES = FAQ_DATA.filter(f => FAQ_SCHEMA_IDS.includes(f.id));

// Single @graph: WebPage (FAQ), BreadcrumbList, FAQPage — one script, full semantics
const PAGE_SCHEMA_JSON = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${FAQ_URL}#webpage`,
      url: FAQ_URL,
      name: `FAQ | ${BRAND_NAME}`,
      description: `Answers to common questions about ${BRAND_NAME} orders, free shipping above 500, temporary no-return policy, sizing, and account management.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${FAQ_URL}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${FAQ_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: FAQ_URL },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${FAQ_URL}#faq`,
      mainEntity: FAQ_SCHEMA_ENTRIES.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
  ],
});

// ── Inline SVG icons — no bundle dependency ────────────────────────────────
function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function IconTruck() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  );
}

function IconReturn() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polyline points="1 4 1 10 7 10"/>
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
    </svg>
  );
}

function IconRuler() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M21.3 8.7l-9-9a1 1 0 0 0-1.4 0l-9 9a1 1 0 0 0 0 1.4l9 9a1 1 0 0 0 1.4 0l9-9a1 1 0 0 0 0-1.4z"/>
      <line x1="9" y1="15" x2="15" y2="9"/>
      <line x1="9.5" y1="9.5" x2="10.5" y2="10.5"/>
      <line x1="12.5" y1="12.5" x2="13.5" y2="13.5"/>
    </svg>
  );
}

function IconMessage() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

// Quick links config — correct icons per destination
const QUICK_LINKS = [
  { to: '/shipping',  icon: <IconTruck  />, title: 'Shipping & Delivery', text: 'Delivery timelines, tracking, and free shipping above 500' },
  { to: '/contact',   icon: <IconReturn />, title: 'Returns Policy',       text: 'Returns are currently unavailable. Contact support for help.' },
  { to: '/size-guide',icon: <IconRuler  />, title: 'Size Guide',           text: 'Size charts and measurement instructions' },
  { to: '/contact',   icon: <IconMessage/>, title: 'Contact Us',           text: 'Get in touch via WhatsApp or email' },
];

// ── Accordion Item ─────────────────────────────────────────────────────────
function AccordionItem({ faq, isOpen, onToggle }) {
  const answerId = useId();
  const questionId = useId();

  return (
    <div
      className={`faq-item${isOpen ? ' faq-item--open' : ''}`}
    >
      <button
        type="button"
        id={questionId}
        className="faq-item__question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span>{faq.q}</span>
        <span className="faq-item__chevron" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      <div
        id={answerId}
        role="region"
        aria-labelledby={questionId}
        className="faq-item__body"
        hidden={!isOpen}
      >
        <div className="faq-item__answer">
          <p>{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [openItems,      setOpenItems]      = useState(new Set());

  const toggleItem = useCallback((id) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setActiveCategory('all');
    setOpenItems(new Set());
  }, []);

  // Single useMemo pass: filter + group in one loop
  const groupedFAQs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const groups = new Map();
    for (const faq of FAQ_DATA) {
      if (activeCategory !== 'all' && faq.category !== activeCategory) continue;
      if (query && !faq.q.toLowerCase().includes(query) && !faq.a.toLowerCase().includes(query)) continue;

      if (!groups.has(faq.category)) groups.set(faq.category, []);
      groups.get(faq.category).push(faq);
    }
    return groups;
  }, [activeCategory, searchQuery]);

  const totalCount = useMemo(
    () => [...groupedFAQs.values()].reduce((s, arr) => s + arr.length, 0),
    [groupedFAQs]
  );

  const visibleFaqIds = useMemo(
    () => [...groupedFAQs.values()].flat().map((faq) => faq.id),
    [groupedFAQs]
  );

  const expandAll = useCallback(() => {
    setOpenItems(new Set(visibleFaqIds));
  }, [visibleFaqIds]);

  const collapseAll = useCallback(() => {
    setOpenItems(new Set());
  }, []);

  const pageTitle = `FAQ | ${BRAND_NAME}`;
  const pageDesc  = `Answers to common questions about ${BRAND_NAME} orders, free shipping above 500, temporary no-return policy, sizing, and account management.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={FAQ_URL} />

        <link rel="prefetch" href="/contact" as="document" />
        <link rel="prefetch" href="/shipping" as="document" />
        <link rel="prefetch" href="/size-guide" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={FAQ_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`FAQ — ${BRAND_NAME} Orders, Shipping & Returns`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`FAQ — ${BRAND_NAME} Orders, Shipping & Returns`} />

        <script type="application/ld+json">{PAGE_SCHEMA_JSON}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="faq-page" id="main-content">

          {/* ── Breadcrumb ─── */}
          <nav className="faq-breadcrumb" aria-label="Breadcrumb">
            <ol className="faq-breadcrumb__list">
              <li>
                <Link to="/" className="faq-breadcrumb__link">Home</Link>
              </li>
              {/* Valid HTML: non-void <li> with aria-hidden content */}
              <li aria-hidden="true" className="faq-breadcrumb__sep">
                <span>›</span>
              </li>
              <li>
                <span aria-current="page" className="faq-breadcrumb__current">
                  FAQ
                </span>
              </li>
            </ol>
          </nav>

          {/* ── Two-column desktop layout ─── */}
          <div className="faq-layout">

            {/* ─── Sidebar (desktop only) ─── */}
            <aside className="faq-sidebar" aria-label="FAQ categories">
              <div className="faq-sidebar__inner">
                <p className="faq-sidebar__heading" aria-hidden="true">Categories</p>
                <nav aria-label="Filter FAQs by category">
                  <ul className="faq-sidebar__nav">
                    {FAQ_CATEGORIES.map(cat => (
                      <li key={cat.id}>
                        <button
                          type="button"
                          className={`faq-sidebar__btn${activeCategory === cat.id ? ' is-active' : ''}`}
                          onClick={() => setActiveCategory(cat.id)}
                          aria-pressed={activeCategory === cat.id}
                        >
                          {cat.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Sticky contact nudge in sidebar */}
                <div className="faq-sidebar__contact">
                  <p className="faq-sidebar__contact-text">
                    Can't find what you're looking for?
                  </p>
                  <Link to="/contact" className="faq-sidebar__contact-link">
                    Contact us →
                  </Link>
                </div>
              </div>
            </aside>

            {/* ─── Main content ─── */}
            <div className="faq-main">

              {/* Page header */}
              <header className="faq-header">
                <h1 className="faq-header__title">
                  Frequently Asked Questions
                </h1>
                <p className="faq-header__subtitle">
                  Answers about jewelry orders, shipping, temporary returns policy, sizing, and more.
                </p>
              </header>

              {/* Search — <form> for semantics + enter key support */}
              <form
                className="faq-search"
                role="search"
                aria-label="Search FAQs"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="faq-search__wrapper">
                  <span className="faq-search__icon" aria-hidden="true">
                    <IconSearch />
                  </span>
                  <input
                    type="search"
                    className="faq-search__input"
                    placeholder="Search questions…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search frequently asked questions"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="faq-search__clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                        aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
              </form>

              {/* Mobile-only category pills (hidden on desktop — sidebar handles it) */}
              <div
                className="faq-pills"
                role="group"
                aria-label="Filter by category"
              >
                {FAQ_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`faq-pill${activeCategory === cat.id ? ' is-active' : ''}`}
                    onClick={() => setActiveCategory(cat.id)}
                    aria-pressed={activeCategory === cat.id}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Tools: result count + expand/collapse */}
              <div className="faq-tools" aria-live="polite">
                <p className="faq-tools__count">
                  {totalCount} question{totalCount !== 1 ? 's' : ''} found
                </p>
                <div className="faq-tools__actions">
                  <button
                    type="button"
                    className="faq-tools__btn"
                    onClick={expandAll}
                    disabled={totalCount === 0 || openItems.size === visibleFaqIds.length}
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    className="faq-tools__btn faq-tools__btn--ghost"
                    onClick={collapseAll}
                    disabled={openItems.size === 0}
                  >
                    Collapse all
                  </button>
                </div>
              </div>

              {/* FAQ content */}
              <div className="faq-content" aria-live="polite" aria-atomic="false">
                {totalCount === 0 ? (
                  <div className="faq-empty" role="status">
                    <p className="faq-empty__text">
                      No questions found
                      {searchQuery ? ` for "${searchQuery}"` : ''}.
                    </p>
                    <button
                      type="button"
                      className="faq-empty__btn"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  [...groupedFAQs.entries()].map(([catId, faqs]) => (
                    <section
                      key={catId}
                      className="faq-section"
                      aria-labelledby={`faq-cat-${catId}`}
                    >
                      {activeCategory === 'all' && (
                        <h2
                          id={`faq-cat-${catId}`}
                          className="faq-section__title"
                        >
                          {CATEGORY_MAP.get(catId) ?? catId}
                        </h2>
                      )}
                      <div className="faq-list">
                        {faqs.map(faq => (
                          <AccordionItem
                            key={faq.id}
                            faq={faq}
                            isOpen={openItems.has(faq.id)}
                            onToggle={() => toggleItem(faq.id)}
                          />
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>

              {/* Quick Links — correct icons per destination */}
              <section className="faq-quick-links" aria-labelledby="faq-ql-heading">
                <h2 id="faq-ql-heading" className="faq-quick-links__title">
                  Still have questions?
                </h2>
                <p className="faq-quick-links__subtitle">
                  Explore these pages or contact us directly
                </p>
                <div className="faq-quick-links__grid">
                  {QUICK_LINKS.map(({ to, icon, title, text }) => (
                    <Link
                      key={`${to}-${title}`}
                      to={to}
                      className="faq-quick-links__card"
                      aria-label={title}
                    >
                      <span className="faq-quick-links__card-icon">{icon}</span>
                      <div className="faq-quick-links__card-body">
                        <h3 className="faq-quick-links__card-title">{title}</h3>
                        <p  className="faq-quick-links__card-text">{text}</p>
                      </div>
                      <span className="faq-quick-links__card-arrow">
                        <IconArrowRight />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>

              {/* ── Related (internal linking) ───────────────────────────── */}
              <nav className="faq-related" aria-label="Related information">
                <h2 className="faq-related__title">Related information</h2>
                <ul className="faq-related__list" role="list">
                  <li><Link to="/shipping" className="faq-related__link">Shipping &amp; Delivery</Link></li>
                  <li><Link to="/contact" className="faq-related__link">Returns Policy Support</Link></li>
                  <li><Link to="/contact" className="faq-related__link">Contact Us</Link></li>
                  <li><Link to="/size-guide" className="faq-related__link">Size Guide</Link></li>
                  <li><Link to="/about" className="faq-related__link">About Us</Link></li>
                  <li><Link to="/shop" className="faq-related__link">Shop All</Link></li>
                </ul>
              </nav>

            </div>{/* /faq-main */}
          </div>{/* /faq-layout */}

        </main>
      </Layout>
    </>
  );
}